import React, { useState, useEffect } from 'react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, DropdownSection, Button } from '@heroui/react';
import { ChevronDown, MapPin, Building, Globe, Search, Home } from 'lucide-react';

const VisibilitySelector = ({ user, onVisibilityChange }) => {
  const [visibilityOptions, setVisibilityOptions] = useState([]);
  const [discoverAreas, setDiscoverAreas] = useState([]);
  const [currentLevel, setCurrentLevel] = useState('area');
  const [exploredArea, setExploredArea] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDiscover, setLoadingDiscover] = useState(false);

  useEffect(() => {
    fetchVisibilityOptions();
    fetchDiscoverAreas();
  }, []);

  const fetchVisibilityOptions = async () => {
    try {
      const response = await fetch('/api/v1/users/me/visibility_options', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVisibilityOptions(data.visibility_options || []);
        setCurrentLevel(data.current_level || 'area');
      } else {
        console.warn('Failed to fetch visibility options:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch visibility options:', error);
    }
  };

  const fetchDiscoverAreas = async () => {
    setLoadingDiscover(true);
    try {
      const response = await fetch('/api/v1/areas/discover?limit=10', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDiscoverAreas(data.areas || []);
      } else {
        console.warn('Failed to fetch discover areas:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch discover areas:', error);
    } finally {
      setLoadingDiscover(false);
    }
  };

  const updateVisibility = async (level) => {
    if (level === currentLevel) return; // Don't refetch if same level
    
    setLoading(true);
    try {
      const response = await fetch('/api/v1/users/me/visibility', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ visibility_level: level })
      });
      if (response.ok) {
        const selectedOption = visibilityOptions.find(opt => opt.level === level);
        setCurrentLevel(level);
        setExploredArea(null); // Clear explored area when changing visibility
        onVisibilityChange?.(level, selectedOption);
      }
    } catch (error) {
      console.error('Failed to update visibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const exploreArea = (area) => {
    const exploreOption = {
      level: 'explore_area',
      name: area.name,
      type: 'area',
      area_id: area._id
    };
    setCurrentLevel('explore_area');
    setExploredArea(exploreOption);
    onVisibilityChange?.('explore_area', exploreOption);
  };

  const returnToHome = () => {
    const homeOption = visibilityOptions.find(opt => opt.level === 'area');
    if (homeOption) {
      setCurrentLevel(homeOption.level);
      setExploredArea(null);
      onVisibilityChange?.(homeOption.level, homeOption);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'area':
      case 'parent_area':
        return <MapPin className="w-3 h-3" />;
      case 'city':
        return <Building className="w-3 h-3" />;
      case 'state':
        return <Globe className="w-3 h-3" />;
      default:
        return <MapPin className="w-3 h-3" />;
    }
  };

  const getCurrentOption = () => {
    if (currentLevel === 'explore_area' && exploredArea) {
      return { name: `🔍 ${exploredArea.name}`, level: 'explore_area' };
    }
    return visibilityOptions.find(opt => opt.level === currentLevel) || 
           { name: user?.area?.name || 'Área', level: 'area' };
  };

  const handleAction = (key) => {
    if (key === 'return_home') {
      returnToHome();
    } else if (key.startsWith('explore_')) {
      const areaId = key.replace('explore_', '');
      const area = discoverAreas.find(a => a._id === areaId);
      if (area) {
        exploreArea(area);
      }
    } else {
      updateVisibility(key);
    }
  };

  return (
    <Dropdown placement="bottom-start">
      <DropdownTrigger>
        <Button
          variant="light"
          size="sm"
          className="h-auto p-1 min-w-0 text-xs text-gray-500 hover:text-gray-700"
          isLoading={loading}
        >
          <span className="truncate max-w-24">{getCurrentOption().name}</span>
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Visibility Options"
        onAction={handleAction}
      >
        {/* Return Home option when exploring */}
        {currentLevel === 'explore_area' && (
          <DropdownSection showDivider>
            <DropdownItem
              key="return_home"
              startContent={<Home className="w-3 h-3" />}
            >
              Voltar para Casa
            </DropdownItem>
          </DropdownSection>
        )}

        {/* Current area options */}
        {visibilityOptions.length > 0 ? (
          <DropdownSection showDivider={discoverAreas.length > 0}>
            {visibilityOptions.map((option) => (
              <DropdownItem
                key={option.level}
                startContent={getIcon(option.type)}
                className={currentLevel === option.level && currentLevel !== 'explore_area' ? 'bg-blue-50' : ''}
              >
                {option.name}
              </DropdownItem>
            ))}
          </DropdownSection>
        ) : (
          <DropdownSection>
            <DropdownItem key="loading" isDisabled>
              Carregando...
            </DropdownItem>
          </DropdownSection>
        )}

        {/* Explore other areas section */}
        {discoverAreas.length > 0 && (
          <DropdownSection title="Explorar Outras Áreas">
            {discoverAreas.map((area) => (
              <DropdownItem
                key={`explore_${area._id}`}
                startContent={<MapPin className="w-3 h-3 text-blue-500" />}
                description={area.city_name && area.state_name ? `${area.city_name}, ${area.state_name}` : undefined}
              >
                <div className="flex justify-between items-center w-full">
                  <span>{area.name}</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {area.recent_post_count}
                  </span>
                </div>
              </DropdownItem>
            ))}
          </DropdownSection>
        )}

        {/* Loading indicator for discover areas */}
        {loadingDiscover && (
          <DropdownSection>
            <DropdownItem key="loading_discover" isDisabled>
              Carregando áreas...
            </DropdownItem>
          </DropdownSection>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export default VisibilitySelector;
